import { Controller } from 'stimulus';

// Cue-by-cue caption editor for the admin video transcriptions tab.
// Collects the edited cues, posts them as JSON and reports the result.
export default class extends Controller {
  static targets = ['readonly', 'editor', 'cues', 'row', 'status', 'saveButton', 'toggleButton'];

  static values = {
    saveUrl: String,
    languageCode: String,
  };

  toggle(event) {
    event.preventDefault();

    const willEdit = this.editorTarget.classList.contains('is-hidden');
    // Switching back to view after saving: the read-only transcript was rendered
    // with the old captions, so reload to show the freshly saved/synced version.
    if (!willEdit && this._savedSinceOpen) {
      window.location.reload();
      return;
    }

    if (this.hasReadonlyTarget) this.readonlyTarget.classList.toggle('is-hidden');
    this.editorTarget.classList.toggle('is-hidden');
    const editing = !this.editorTarget.classList.contains('is-hidden');
    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.textContent = editing ? 'Back to view' : 'Edit captions';
    }
    // Textareas can only be measured once visible, so size them on open.
    if (editing) this._autogrowAll();
  }

  // Reveal/hide the start/end inputs for a single cue.
  toggleTiming(event) {
    event.preventDefault();
    const row = event.currentTarget.closest('[data-transcript-editor-target="row"]');
    if (!row) return;
    const timing = row.querySelector('.transcript-editor__timing');
    if (timing) {
      timing.classList.toggle('is-hidden');
      event.currentTarget.classList.toggle('is-active', !timing.classList.contains('is-hidden'));
    }
  }

  // Keep the compact chip label in sync while editing the start time.
  syncTimecode(event) {
    const row = event.currentTarget.closest('[data-transcript-editor-target="row"]');
    if (!row) return;
    const chip = row.querySelector('.transcript-editor__timecode');
    if (chip) chip.textContent = event.currentTarget.value.trim().replace(/^00:/, '');
  }

  autogrow(event) {
    this._resize(event.currentTarget);
  }

  _autogrowAll() {
    this.element
      .querySelectorAll('.transcript-editor__text')
      .forEach((el) => this._resize(el));
  }

  _resize(el) {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }

  save(event) {
    event.preventDefault();

    const cues = this.rowTargets
      .map((row) => ({
        start: this._fieldValue(row, 'start'),
        end: this._fieldValue(row, 'end'),
        text: this._fieldValue(row, 'text'),
      }))
      .filter((cue) => cue.text.length > 0);

    if (cues.length === 0) {
      this._setStatus('Add at least one caption with text before saving.', 'is-danger');
      return;
    }

    this.saveButtonTarget.classList.add('is-loading');
    this._setStatus('Saving…', 'is-info');

    fetch(this.saveUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': this._csrfToken(),
      },
      body: JSON.stringify({ language_code: this.languageCodeValue, cues }),
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (ok) {
          this._savedSinceOpen = true;
          const synced = data.synced_to_mux ? ' Captions are being updated for the player.' : '';
          this._setStatus(`Saved.${synced}`, 'is-success');
        } else {
          this._setStatus(data.error || 'Could not save captions.', 'is-danger');
        }
      })
      .catch(() => this._setStatus('Network error while saving captions.', 'is-danger'))
      .finally(() => this.saveButtonTarget.classList.remove('is-loading'));
  }

  _fieldValue(row, field) {
    const input = row.querySelector(`[data-field="${field}"]`);
    return input ? input.value.trim() : '';
  }

  _setStatus(message, modifier) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.textContent = message;
    this.statusTarget.className = `transcript-editor__status help ${modifier}`;
  }

  _csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute('content') : '';
  }
}
